import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Loader2, CheckCircle2, XCircle, Award, Sparkles, BookOpen, Clock3 } from "lucide-react";

function formatBRL(value) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

function Countdown({ expiresAt }) {
    const [left, setLeft] = useState(() => Math.max(0, new Date(expiresAt) - new Date()));
    useEffect(() => {
        if (!expiresAt) return;
        const id = setInterval(() => setLeft(Math.max(0, new Date(expiresAt) - new Date())), 1000);
        return () => clearInterval(id);
    }, [expiresAt]);
    if (!expiresAt || left <= 0) return null;
    const m = Math.floor(left / 60000);
    const s = Math.floor((left % 60000) / 1000);
    return (
        <span data-testid="upsell-countdown" className="inline-flex items-center gap-2 text-[#D4AF37] font-display">
            <Clock3 className="w-4 h-4" />
            {String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
        </span>
    );
}

function UpsellSection() {
    const [data, setData] = useState(null);
    const [buying, setBuying] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/billing/upsell").then((r) => setData(r.data)).catch(() => {});
    }, []);

    const buy = async (ebookId) => {
        setError("");
        setBuying(ebookId);
        try {
            const { data } = await api.post("/billing/checkout/ebook", {
                ebook_id: ebookId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            setError(formatApiError(e));
            setBuying(null);
        }
    };

    if (!data || !data.items?.length) return null;

    const eligible = data.eligible;

    return (
        <section data-testid="upsell-section" className="mt-14">
            <div className="text-center">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                    <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
                    <span className="overline text-[#D4AF37]">
                        {eligible ? `Oferta exclusiva · ${data.discount_pct}% off por tempo limitado` : "Biblioteca digital"}
                    </span>
                </div>
                <h2 className="font-display font-light text-4xl md:text-5xl mt-5 tracking-tight">
                    Aprofunde sua jornada com <span className="font-editorial italic text-holo">e-books</span>
                </h2>
                {eligible && (
                    <p className="font-body text-[#5C5C5C] mt-3">
                        Esta oferta expira em <Countdown expiresAt={data.expires_at} />. Aproveite agora.
                    </p>
                )}
            </div>

            <div className="grid md:grid-cols-3 gap-6 mt-10 text-left">
                {data.items.map((eb) => (
                    <article
                        key={eb.id}
                        data-testid={`upsell-card-${eb.id}`}
                        className="rounded-3xl bg-white border border-black/5 overflow-hidden flex flex-col hover:shadow-xl hover:shadow-black/5 transition-all"
                    >
                        <div className="aspect-[5/4] bg-[#F2EFEA] overflow-hidden">
                            {eb.cover && <img src={eb.cover} alt={eb.title} className="w-full h-full object-cover" loading="lazy" />}
                        </div>
                        <div className="p-6 flex-1 flex flex-col">
                            <div className="overline text-[#5C5C5C]">{eb.category}</div>
                            <h3 className="font-display text-xl mt-2 tracking-tight">{eb.title}</h3>
                            <p className="font-body text-sm text-[#5C5C5C] mt-3 flex-1 line-clamp-3">{eb.description}</p>

                            <div className="mt-5 flex items-baseline gap-3">
                                <span className="font-display text-2xl text-[#1A1A1A]">{formatBRL(eb.amount)}</span>
                                {eligible && eb.discount_pct > 0 && (
                                    <span className="font-body text-sm text-[#9A9A9A] line-through">{formatBRL(eb.base_amount)}</span>
                                )}
                            </div>

                            {eb.purchased ? (
                                <button data-testid={`upsell-owned-${eb.id}`} className="mt-5 btn-ghost justify-center" disabled>
                                    <CheckCircle2 className="w-4 h-4 text-[#00D4D4]" /> Você já possui
                                </button>
                            ) : (
                                <button
                                    data-testid={`upsell-buy-${eb.id}`}
                                    onClick={() => buy(eb.id)}
                                    disabled={buying === eb.id}
                                    className="mt-5 btn-primary justify-center"
                                >
                                    {buying === eb.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
                                    {buying === eb.id ? "Redirecionando..." : "Garantir agora"}
                                </button>
                            )}
                        </div>
                    </article>
                ))}
            </div>

            {error && (
                <div data-testid="upsell-error" className="mt-5 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>
            )}
        </section>
    );
}

export default function PaymentReturn() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "polling", attempts: 0, payload: null });

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "missing", attempts: 0, payload: null });
            return;
        }
        let cancelled = false;
        const maxAttempts = 8;
        const poll = async (n = 1) => {
            if (cancelled) return;
            try {
                const { data } = await api.get(`/billing/status/${sessionId}`);
                if (cancelled) return;
                if (data.payment_status === "paid") {
                    setState({ status: "paid", attempts: n, payload: data });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", attempts: n, payload: data });
                    return;
                }
                if (n >= maxAttempts) {
                    setState({ status: "timeout", attempts: n, payload: data });
                    return;
                }
                setState({ status: "polling", attempts: n, payload: data });
                setTimeout(() => poll(n + 1), 2000);
            } catch {
                if (cancelled) return;
                if (n >= maxAttempts) {
                    setState({ status: "error", attempts: n, payload: null });
                    return;
                }
                setTimeout(() => poll(n + 1), 2500);
            }
        };
        poll(1);
        return () => { cancelled = true; };
    }, [sessionId]);

    const isEbook = state.payload?.kind === "ebook";

    return (
        <div className="min-h-screen relative grain">
            <Header />
            <main className="max-w-[1100px] mx-auto px-6 pt-24 pb-24">
                <div data-testid={`payment-state-${state.status}`} className="bg-white rounded-3xl border border-black/5 p-10 md:p-14 text-center max-w-[700px] mx-auto">
                    {state.status === "polling" && (
                        <>
                            <Loader2 className="w-10 h-10 text-[#5C5C5C] animate-spin mx-auto" />
                            <h1 className="font-display text-3xl mt-6 tracking-tight">Confirmando seu pagamento...</h1>
                            <p className="font-body text-[#5C5C5C] mt-3">Não feche esta janela. Vamos liberar seu acesso em segundos.</p>
                        </>
                    )}
                    {state.status === "paid" && (
                        <>
                            <div className="w-16 h-16 rounded-full bg-[#00D4D4]/15 border border-[#00D4D4]/40 flex items-center justify-center mx-auto">
                                <CheckCircle2 className="w-8 h-8 text-[#00D4D4]" />
                            </div>
                            <div className="overline text-[#5C5C5C] mt-6">Pagamento confirmado</div>
                            <h1 className="font-display font-light text-4xl mt-2 tracking-tight">
                                {isEbook ? (
                                    <>E-book <span className="font-editorial italic text-holo">liberado</span></>
                                ) : (
                                    <>Bem-vindo ao <span className="font-editorial italic text-holo">Instituto</span></>
                                )}
                            </h1>
                            <p className="font-body text-[#5C5C5C] mt-4">
                                {isEbook
                                    ? <>Seu e-book <span className="text-[#1A1A1A] font-display">{state.payload?.ebook_title}</span> já está disponível na sua biblioteca.</>
                                    : <>Seu acesso ao plano <span className="text-[#1A1A1A] font-display">{state.payload?.plan_id}</span> foi ativado. Boas práticas!</>
                                }
                            </p>
                            <div className="flex flex-wrap gap-3 justify-center mt-8">
                                <Link to={isEbook ? "/biblioteca" : "/inicio"} className="btn-primary" data-testid="paid-primary-cta">
                                    {isEbook ? "Ir para a biblioteca" : "Começar a explorar"}
                                </Link>
                                <Link to="/perfil" className="btn-ghost"><Award className="w-4 h-4" /> Ver meu espaço</Link>
                            </div>
                        </>
                    )}
                    {(state.status === "expired" || state.status === "timeout" || state.status === "error" || state.status === "missing") && (
                        <>
                            <div className="w-16 h-16 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center mx-auto">
                                <XCircle className="w-8 h-8 text-amber-700" />
                            </div>
                            <h1 className="font-display text-3xl mt-6 tracking-tight">
                                {state.status === "missing" ? "Sessão não encontrada" : "Não conseguimos confirmar agora"}
                            </h1>
                            <p className="font-body text-[#5C5C5C] mt-3">
                                {state.status === "timeout"
                                    ? "Pode levar alguns minutos. Se o valor foi cobrado, seu acesso será liberado automaticamente assim que a operadora confirmar."
                                    : "Tente novamente, ou contate o suporte se o valor já foi cobrado."}
                            </p>
                            <div className="flex flex-wrap gap-3 justify-center mt-8">
                                <Link to="/planos" className="btn-primary">Voltar aos planos</Link>
                                <Link to="/inicio" className="btn-ghost">Ir para o início</Link>
                            </div>
                        </>
                    )}
                </div>

                {state.status === "paid" && !isEbook && <UpsellSection />}
            </main>
            <Footer />
        </div>
    );
}
